import { wrapInList } from 'prosemirror-schema-list';
import { toggleMark } from 'prosemirror-commands';
import { Dropdown, MenuItem } from 'prosemirror-menu';
import { undo, redo } from 'prosemirror-history';
import { openPrompt } from '../prompt';
import { TextField } from '../TextField';
import {
  alignmentItem,
  applyMark,
  blockTypeIsActive,
  cmdItem,
  markItem,
  toggleBlockType,
} from './common';
import icons from '../icons';
import { markActive } from '../utils';

const TEXT_COLORS = [
  '#26292b',
  '#FF0000',
  '#00FF00',
  '#0000FF',
  '#FFFF00',
  '#FF00FF',
  '#00FFFF',
  '#808080',
  '#800000',
  '#808000',
  '#008000',
  '#800080',
  '#008080',
  '#000080',
  '#FFA500',
  '#A52A2A',
];

const wrapListItem = (nodeType, options) =>
  cmdItem(wrapInList(nodeType, options.attrs), options);

const imageUploadItem = (nodeType, onImageUpload) =>
  new MenuItem({
    title: 'Upload image',
    icon: icons.image,
    enable() {
      return true;
    },
    run() {
      onImageUpload();
      return true;
    },
  });

const headerItem = (nodeType, options) => {
  const { level = 1 } = options;
  return new MenuItem({
    title: `Heading ${level}`,
    icon: options.icon,
    active(state) {
      return blockTypeIsActive(state, nodeType, { level });
    },
    enable() {
      return true;
    },
    run(state, dispatch, view) {
      if (blockTypeIsActive(state, nodeType, { level })) {
        toggleBlockType(nodeType, { level })(state, dispatch);
        return true;
      }

      toggleBlockType(nodeType, { level })(view.state, view.dispatch);
      view.focus();

      return false;
    },
  });
};

const linkItem = markType =>
  new MenuItem({
    title: 'Add or remove link',
    icon: icons.link,
    active(state) {
      return markActive(state, markType);
    },
    enable(state) {
      return !state.selection.empty;
    },
    run(state, dispatch, view) {
      if (markActive(state, markType)) {
        toggleMark(markType)(state, dispatch);
        return true;
      }
      openPrompt({
        title: 'Create a link',
        fields: {
          href: new TextField({
            label: 'https://example.com',
            class: 'small',
            required: true,
          }),
        },
        callback(attrs) {
          toggleMark(markType, attrs)(view.state, view.dispatch);
          view.focus();
        },
      });
      return false;
    },
  });

const fontFamilyItem = (markType, family) => {
  return new MenuItem({
    title: `Set font family to ${family}`,
    label: family,
    render() {
      const wrapper = document.createElement('div');
      wrapper.style.width = '100%'; // Make the wrapper take up 100% of the width
      wrapper.style.display = 'flex';
      wrapper.style.justifyContent = 'flex-start'; // Align text to the left
      wrapper.style.padding = '4px';

      const label = document.createElement('span');
      label.textContent = family;
      label.style.fontFamily = family; // Apply the font family to the label
      label.style.cursor = 'pointer';

      wrapper.appendChild(label);
      return wrapper;
    },
    run: applyMark(markType, 'family', family),
    active(state) {
      const { from, to } = state.selection;
      let isActive = false;
      state.doc.nodesBetween(from, to, node => {
        if (node.marks) {
          node.marks.forEach(mark => {
            if (mark.type === markType && mark.attrs.family === family) {
              isActive = true;
            }
          });
        }
      });
      return isActive;
    },
  });
};

const fontFamilyDropdown = markType => {
  const FONT_FAMILIES = [
    'Arial',
    'Courier New',
    'Georgia',
    'Times New Roman',
    'Verdana',
    'Helvetica',
    'Helvetica Neue',
    'San Francisco',
    'Monaco',
    'Lucida Grande',
    'Geneva',
    'Apple Chancery',
    'Brush Script MT',
    'Palatino',
    'Didot',
    'Futura',
    'Gill Sans',
  ];
  const items = FONT_FAMILIES.map(family => fontFamilyItem(markType, family));
  return new Dropdown(items, { label: 'Font' });
};

const fontSizeItem = (markType, size) => {
  return new MenuItem({
    title: `Set font size to ${size}px`,
    label: `${size}px`,
    render() {
      const wrapper = document.createElement('div');
      wrapper.style.width = '100%'; // Make the wrapper take up 100% of the width
      wrapper.style.display = 'flex';
      wrapper.style.justifyContent = 'flex-start'; // Align text to the left
      wrapper.style.padding = '4px';

      const label = document.createElement('span');
      label.textContent = `${size}px`;
      label.style.fontSize = `${size}px`; // Apply the font size to the label
      label.style.cursor = 'pointer';

      wrapper.appendChild(label);
      return wrapper;
    },
    run: applyMark(markType, 'size', size),
    active(state) {
      const { from, to } = state.selection;
      let isActive = false;
      state.doc.nodesBetween(from, to, node => {
        if (node.marks) {
          node.marks.forEach(mark => {
            if (mark.type === markType && mark.attrs.size === size) {
              isActive = true;
            }
          });
        }
      });
      return isActive;
    },
  });
};

const fontSizeDropdown = markType => {
  const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32];
  const items = FONT_SIZES.map(size => fontSizeItem(markType, size));
  return new Dropdown(items, { label: 'Font Size' });
};

const colorPickerDropdown = (markType, colors) => {
  const items = colors.map(color => {
    return new MenuItem({
      title: `Set text color to ${color}`,
      label: '',
      render() {
        const wrapper = document.createElement('div');
        wrapper.style.width = '100%'; // Make the wrapper take up 100% of the width
        wrapper.style.display = 'flex';
        wrapper.style.justifyContent = 'center'; // Center the button inside the div

        const button = document.createElement('button');
        button.style.backgroundColor = color;
        button.style.width = '24px';
        button.style.height = '24px';
        button.style.border = '1px solid #ccc';
        button.style.margin = '2px';
        button.style.cursor = 'pointer';
        button.title = `Set text color to ${color}`;

        wrapper.appendChild(button); // Wrap the button inside the div
        return wrapper;
      },
      run: applyMark(markType, 'color', color),
      active(state) {
        const { from, to } = state.selection;
        let isActive = false;
        state.doc.nodesBetween(from, to, node => {
          if (node.marks) {
            node.marks.forEach(mark => {
              if (mark.type === markType && mark.attrs.color === color) {
                isActive = true;
              }
            });
          }
        });
        return isActive;
      },
    });
  });

  return new Dropdown(items, {
    label: 'Text Color',
    class: 'text-color-menu',
  });
};

const buildMenuOptions = (
  schema,
  {
    enabledMenuOptions = [
      'strong',
      'em',
      'code',
      'link',
      'undo',
      'redo',
      'bulletList',
      'orderedList',
      'alignLeft',
      'alignCenter',
      'alignRight',
      'alignJustify',
      'strike',
      'fontSize',
      'fontFamily',
      'textColor',
    ],
    onImageUpload = () => {},
  }
) => {
  const availableMenuOptions = {
    strong: markItem(schema.marks.strong, {
      title: 'Toggle strong style',
      icon: icons.strong,
    }),
    em: markItem(schema.marks.em, {
      title: 'Toggle emphasis',
      icon: icons.em,
    }),
    code: markItem(schema.marks.code, {
      title: 'Toggle code font',
      icon: icons.code,
    }),
    link: linkItem(schema.marks.link),
    bulletList: wrapListItem(schema.nodes.bullet_list, {
      title: 'Wrap in bullet list',
      icon: icons.bulletList,
    }),
    orderedList: wrapListItem(schema.nodes.ordered_list, {
      title: 'Wrap in ordered list',
      icon: icons.orderedList,
    }),
    undo: new MenuItem({
      title: 'Undo last change',
      run: undo,
      enable: state => undo(state),
      icon: icons.undo,
    }),
    redo: new MenuItem({
      title: 'Redo last undone change',
      run: redo,
      enable: state => redo(state),
      icon: icons.redo,
    }),
    h1: headerItem(schema.nodes.heading, {
      level: 1,
      title: 'Toggle code font',
      icon: icons.h1,
    }),
    h2: headerItem(schema.nodes.heading, {
      level: 2,
      title: 'Toggle code font',
      icon: icons.h2,
    }),
    h3: headerItem(schema.nodes.heading, {
      level: 3,
      title: 'Toggle code font',
      icon: icons.h3,
    }),
    imageUpload: imageUploadItem(schema.nodes.image, onImageUpload),
    alignLeft: alignmentItem(schema.nodes.paragraph, 'left'),
    alignCenter: alignmentItem(schema.nodes.paragraph, 'center'),
    alignRight: alignmentItem(schema.nodes.paragraph, 'right'),
    alignJustify: alignmentItem(schema.nodes.paragraph, 'justify'),
    fontSize: fontSizeDropdown(schema.marks.fontSize),
    fontFamily: fontFamilyDropdown(schema.marks.fontFamily),
    textColor: colorPickerDropdown(schema.marks.textColor, TEXT_COLORS),
  };

  const alwaysEnabledMenuOptions = [
    'alignLeft',
    'alignCenter',
    'alignRight',
    'alignJustify',
    'fontSize',
    'fontFamily',
    'textColor',
  ];
  return [
    enabledMenuOptions
      .concat(alwaysEnabledMenuOptions)
      .filter(menuOptionKey => !!availableMenuOptions[menuOptionKey])
      .map(menuOptionKey => availableMenuOptions[menuOptionKey]),
  ];
};

export default buildMenuOptions;
